// Admin API client. All requests include cookies (the md_studio_session
// cookie set by the bridge handshake) so the backend can resolve the
// requester via studio_admins.

import type {
  AdminMe,
  AuditEntry,
  Template,
  CustomersListResponse,
  CustomerDetail,
  DesignSummary,
  AttachResponse,
  BulkAttachResponse,
  ImpersonateResponse,
} from './types';

interface Config {
  apiBase: string;
  shopDomain: string;
  bridge: {
    customerId: string;
    email: string;
    shopDomain: string;
    ts: number;
    sig: string;
  } | null;
}

declare global {
  interface Window {
    __MD_STUDIO_ADMIN_CONFIG__: Config;
  }
}

const config = window.__MD_STUDIO_ADMIN_CONFIG__;
export const API_BASE = config.apiBase;
export const SHOP_DOMAIN = config.shopDomain;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

/**
 * One-shot bridge handshake — exchanges the Liquid-rendered HMAC payload
 * for an md_studio_session cookie. Called once on SPA boot. Success means
 * subsequent requests automatically authenticate via cookie.
 */
export async function bridgeAuthenticate(): Promise<void> {
  if (!config.bridge) throw new ApiError(0, 'no_bridge', 'Bridge payload missing');
  const res = await fetch(`${API_BASE}/storefront/studio/auth/bridge`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // Liquid template now sends customerId already in GID form
    // ('gid://shopify/Customer/123456'), matching what backend's bridge
    // verifier expects. We pass it through unchanged.
    body: JSON.stringify({
      customerId: config.bridge.customerId,
      email: config.bridge.email,
      shopDomain: config.bridge.shopDomain,
      ts: config.bridge.ts,
      sig: config.bridge.sig,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'bridge_failed' }));
    throw new ApiError(res.status, body.error ?? 'bridge_failed', `Bridge auth failed (${res.status})`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'unknown' }));
    throw new ApiError(res.status, body.error ?? 'unknown', body.message ?? res.statusText, body);
  }
  return (await res.json()) as T;
}

// ── Bootstrap ─────────────────────────────────────────────────────────────

export const getMe = () => request<AdminMe>('/storefront/portal-admin/me');

// ── Audit ─────────────────────────────────────────────────────────────────

export const listAudit = (params: { limit?: number; admin?: string; action?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.admin) qs.set('admin', params.admin);
  if (params.action) qs.set('action', params.action);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<{ entries: AuditEntry[] }>(`/storefront/portal-admin/audit${suffix}`);
};

// ── Templates ─────────────────────────────────────────────────────────────

export const listTemplates = (params: { archived?: boolean; shop?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.archived) qs.set('archived', 'true');
  if (params.shop) qs.set('shop', params.shop);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<{ templates: Template[] }>(`/storefront/portal-admin/templates${suffix}`);
};

export const createTemplate = (body: Record<string, unknown>) =>
  request<{ template: Template }>('/storefront/portal-admin/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const patchTemplate = (id: string, body: Record<string, unknown>) =>
  request<{ template: Template }>(`/storefront/portal-admin/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const duplicateTemplate = (id: string, name?: string) =>
  request<{ template: Template }>(`/storefront/portal-admin/templates/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });

export const refreshThumbnail = (id: string) =>
  request<{ ok: boolean; thumbnail_url: string | null; master_updated_at: string }>(
    `/storefront/portal-admin/templates/${id}/refresh-thumbnail`,
    { method: 'POST', body: JSON.stringify({}) },
  );

export const markTemplateUpdated = (id: string) =>
  request<{ ok: boolean; master_updated_at: string }>(
    `/storefront/portal-admin/templates/${id}/mark-updated`,
    { method: 'POST', body: JSON.stringify({}) },
  );

export const listStaleCustomers = (id: string) =>
  request<{ template_id: string; master_updated_at: string; stale_count: number; designs: unknown[] }>(
    `/storefront/portal-admin/templates/${id}/stale-customers`,
  );

// ── Customers ─────────────────────────────────────────────────────────────

export const listCustomers = (params: {
  q?: string;
  shop?: string;
  limit?: number;
  offset?: number;
  has_designs?: boolean;
} = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.shop) qs.set('shop', params.shop);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.has_designs !== undefined) qs.set('has_designs', String(params.has_designs));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<CustomersListResponse>(`/storefront/portal-admin/customers${suffix}`);
};

export const getCustomer = (id: string) =>
  request<CustomerDetail>(`/storefront/portal-admin/customers/${id}`);

export const getCustomerLibrary = (id: string, params: { archived?: boolean; source?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.archived) qs.set('archived', 'true');
  if (params.source) qs.set('source', params.source);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<{ designs: DesignSummary[] }>(
    `/storefront/portal-admin/customers/${id}/library${suffix}`,
  );
};

export const getCustomerActivity = (id: string) =>
  request<{ entries: AuditEntry[] }>(`/storefront/portal-admin/customers/${id}/activity`);

// ── Attach ────────────────────────────────────────────────────────────────

export const attachToCustomer = (body: {
  customer_id: string;
  template_id: string;
  custom_name?: string;
  notify?: boolean;
}) =>
  request<AttachResponse>('/storefront/portal-admin/attach', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const bulkAttach = (body: {
  customer_ids: string[];
  template_id: string;
  notify?: boolean;
  skip_if_already_attached?: boolean;
}) =>
  request<BulkAttachResponse>('/storefront/portal-admin/bulk-attach', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// ── Impersonate ───────────────────────────────────────────────────────────

export const startImpersonation = (customer_id: string) =>
  request<ImpersonateResponse>('/storefront/portal-admin/impersonate', {
    method: 'POST',
    body: JSON.stringify({ customer_id }),
  });
